import type { RouteLocationNormalizedLoaded } from 'vue-router';
import type { infer as zInfer, ZodTypeAny } from 'zod';

interface RouteSchema {
  params?: ZodTypeAny;
  query?: ZodTypeAny;
}

type ValidatedRoute<S extends RouteSchema> = Omit<RouteLocationNormalizedLoaded, 'params' | 'query'> & {
  params: S['params'] extends ZodTypeAny ? zInfer<S['params']> : {};
  query: S['query'] extends ZodTypeAny ? zInfer<S['query']> : {};
};

export const useValidatedRoute = <S extends RouteSchema>(schema: S): ValidatedRoute<S> => {
  const route = useRoute();

  const params = computed(() => {
    try {
      return schema.params?.parse(route.params) ?? {};
    } catch {
      throw createError({ statusCode: 404, message: 'Страница не найдена' });
    }
  });

  const query = computed(() => {
    try {
      return schema.query?.parse(route.query) ?? {};
    } catch {
      throw createError({ statusCode: 404, message: 'Страница не найдена' });
    }
  });

  // возвращаем проксированный route с реактивными params/query
  return new Proxy(route, {
    get(target, prop, receiver) {
      if (prop === 'params') return params.value;
      if (prop === 'query') return query.value;
      return Reflect.get(target, prop, receiver);
    },
  }) as ValidatedRoute<S>;
};
